/**
 * The garment photo: see it, add it, replace it.
 *
 * Until now the wardrobe UI could only *read* photos. `POST /items/:id/photo` has existed in
 * the worker since the module was built and nothing in the frontend ever called it, so every
 * image had to be pushed to R2 by hand with wrangler and linked with a hand-written UPDATE.
 * Twelve went in that way on 11-12 Aug 2026 before the missing control was noticed — Brian's
 * words were "would have uploaded image myself but we dont have that option".
 *
 * The limits mirror `worker/routes/wardrobe.ts` exactly — jpeg/png/webp, 8MB — and are checked
 * here too so a 9MB drop fails instantly instead of after the bytes have gone up the wire.
 * The worker stays the authority; this is a courtesy, not a gate.
 */
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { WardrobeItem } from '../../shared/types';
import { Button, cx, errorText, photoSrc, swatchStyle } from './ui';

/** Same three the route accepts. A HEIC straight off a phone is not one of them. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

export default function PhotoField({
  item,
  onUploaded,
}: {
  item: WardrobeItem;
  onUploaded: (updated: WardrobeItem) => void;
}) {
  // Local, because the parent's `item` prop is the object the form was opened with and is
  // not replaced when the grid reloads. Holding the fresh row here is what makes the preview
  // update the moment the upload lands rather than on the next open.
  const [current, setCurrent] = useState<WardrobeItem>(item);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function send(file: File) {
    setError(null);

    if (!ACCEPTED.includes(file.type)) {
      const what = file.type || 'a file with no type';
      setError(`That is ${what}. It needs to be a JPEG, PNG or WebP — a HEIC from a phone will not do.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`${(file.size / 1024 / 1024).toFixed(1)}MB is over the 8MB limit. Export it smaller.`);
      return;
    }

    const form = new FormData();
    form.append('photo', file);

    setBusy(true);
    try {
      const updated = await api.upload<WardrobeItem>(`/wardrobe/items/${item.id}/photo`, form);
      setCurrent(updated);
      onUploaded(updated);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      // Clearing lets the SAME filename be picked again — otherwise re-selecting the file you
      // just fixed fires no change event at all and the control looks broken.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void send(file);
  }

  const colorText = current.colors.length ? current.colors.join(' / ') : 'colour not recorded';

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium text-ink">Photo</legend>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cx(
          'flex items-center gap-4 rounded-card border border-dashed p-3 transition-colors',
          dragging ? 'border-sage bg-sage-soft' : 'border-border bg-surface-sunk'
        )}
      >
        {/* Same swatch-or-photo rule as the grid tile, so the two never disagree. */}
        <span className="block h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-border">
          {current.has_image ? (
            <img
              src={photoSrc(current)}
              alt={current.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span
              className="flex h-full w-full items-end p-1"
              style={swatchStyle(current.colors)}
              aria-hidden="true"
            >
              <span className="rounded bg-surface/85 px-1 py-0.5 text-[10px] leading-tight text-ink">
                {colorText}
              </span>
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(',')}
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void send(file);
            }}
          />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {busy ? 'Uploading…' : current.has_image ? 'Replace photo' : 'Add a photo'}
          </Button>
          <p className="mt-1.5 text-xs text-ink-soft">
            {dragging
              ? 'Drop it.'
              : 'Or drag one in. JPEG, PNG or WebP, up to 8MB.'}
          </p>
          {current.has_image && (
            <p className="mt-0.5 text-xs text-ink-soft">
              Replacing deletes the old image — there is no undo.
            </p>
          )}
        </div>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-2 rounded-lg border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink"
        >
          {error}
        </p>
      )}
    </fieldset>
  );
}
